<template>
  <div class="header">
    <h2 class="header-text">{{ t('general__text__pois') }}</h2>
    <img
      :id="headerIconId"
      alt="icon-poi-visibility"
      class="header-icon"
      role="button"
      :src="headerIconSrc"
      tabindex="0"
      @click="onToggleAllTypes"
      @keydown.enter="onToggleAllTypes"
      @keydown.space="(event: Event) => onToggleAllTypes(event, true)"
    />
  </div>
  <DropdownCheckboxMapMenu
    :title="title"
    :options="poiOptions"
    :preventCloseElementIds="[headerIconId]"
    @toggleOption="onToggle"
  />
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import DropdownCheckboxMapMenu, {
  optionType
} from '@/components/general/selectors/DropdownMapMenu/checkbox/DropdownCheckboxMapMenu.vue'
import iconVisible from '@/assets/icons-general/icon-visibility-visible.svg'
import iconHidden from '@/assets/icons-general/icon-visibility-hidden.svg'
const store = useStore()
const { t } = useI18n()

// Header content
const title = computed<string>(() => {
  if (!poiTypes.value.length) return t('general__text__selection-no-groups-available')
  return `${t('general__text__select')} ${t('general__text__category').toLowerCase()}`
})
const headerIconSrc = computed<string>(() => {
  return anyPoiVisible.value ? iconVisible : iconHidden
})
const anyPoiVisible = computed<boolean>(() => {
  return poiOptions.value.some((option) => option.checked)
})
const headerIconId = 'poi-header-icon'

// DropdownCheckboxMapMenu content
const poiTypes = computed(() => store.state.iv.poiTypes)

const poiOptions = computed<optionType[]>(() => {
  const options: optionType[] = []
  poiTypes.value.forEach((group) => {
    if (group.name.en === undefined) return
    options.push({
      id: `123456789${group.id.toString()}`,
      checked: group.visible,
      value: group.name.en,
      isHeading: true
    })
    group.types.forEach((type) => {
      if (type.name.en === undefined) return
      options.push({
        id: type.id.toString(),
        checked: type.visible,
        value: type.name.en,
        iconSource: type.iconSrc
      })
    })
  })
  return options
})

// Toggle logic
function onToggle(id: string) {
  const option = poiOptions.value.find((option) => option.id === id)
  if (!option) return
  if (option?.isHeading)
    store.dispatch('iv/togglePoiGroup', { id: parseInt(id.substring(9)), visible: !option.checked })
  else store.dispatch('iv/togglePoiType', { id: parseInt(id), visible: !option.checked })
}
function onToggleAllTypes(event?: Event, preventDefault?: boolean) {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  const updatedVisibility = !anyPoiVisible.value
  poiOptions.value.forEach((option) => {
    if (!option.isHeading) return
    store.dispatch('iv/togglePoiGroup', {
      id: parseInt(option.id.substring(9)),
      visible: updatedVisibility
    })
  })
}
</script>

<style lang="scss" scoped>
@use '@/styles/variables.scss' as old-variables;
.header {
  display: flex;
  justify-content: space-between;
  &-text {
    color: old-variables.$color-text-white;
  }
  &-icon {
    width: 30px;
    cursor: pointer;
  }
}
</style>
